import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import {
  ArrowLeft, RefreshCw, Loader2, Megaphone, ExternalLink, Search,
  CheckCircle2, XCircle, ChevronDown, TrendingUp,
} from "lucide-react";
import { http } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import SocialPulseHeader from "@/components/socialpulse/SocialPulseHeader";
import SocialPulseFooter from "@/components/socialpulse/SocialPulseFooter";

/**
 * /admin/ads — Social Pulse Ad Network dashboard.
 *
 * One screen shows every sister × placement cell with current state +
 * last-24h impression/click counters. Each cell has a toggle that
 * flips the (portal, placement) override and takes effect on the
 * next /api/ads/serve call (sub-millisecond server-side).
 *
 * No campaign manager here yet — v1 serves cross-promo house ads
 * only. Campaign scheduling comes in Step 3.
 */

export default function AdminAds() {
  const { user, isAdmin } = useAuth();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [togglingKey, setTogglingKey] = useState(null);
  const [q, setQ] = useState("");
  const [onlyEnabled, setOnlyEnabled] = useState("all");

  const load = async () => {
    setLoading(true);
    try {
      const r = await http.get("/ads/admin/grid");
      setData(r.data);
    } catch (e) {
      toast.error(e.response?.data?.detail || e.message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { if (user && isAdmin) load(); }, [user, isAdmin]);

  const toggleCell = async (cell) => {
    const key = `${cell.portal}|${cell.placement}`;
    setTogglingKey(key);
    const nextState = !cell.enabled;
    // Optimistic local update so the UI feels instant.
    setData((d) => ({
      ...d,
      cells: d.cells.map((c) => (c.portal === cell.portal && c.placement === cell.placement ? { ...c, enabled: nextState } : c)),
      totals: {
        ...d.totals,
        enabled:  d.totals.enabled  + (nextState ? 1 : -1),
        disabled: d.totals.disabled + (nextState ? -1 : 1),
      },
    }));
    try {
      await http.post("/ads/admin/placement-toggle", {
        portal: cell.portal, placement: cell.placement, enabled: nextState,
      });
      toast.success(`${cell.portal_name} · ${cell.placement} ${nextState ? "ENABLED" : "DISABLED"}`);
    } catch (e) {
      // Rollback on failure.
      setData((d) => ({
        ...d,
        cells: d.cells.map((c) => (c.portal === cell.portal && c.placement === cell.placement ? { ...c, enabled: !nextState } : c)),
        totals: {
          ...d.totals,
          enabled:  d.totals.enabled  + (nextState ? -1 : 1),
          disabled: d.totals.disabled + (nextState ? 1 : -1),
        },
      }));
      toast.error(e.response?.data?.detail || e.message);
    } finally {
      setTogglingKey(null);
    }
  };

  // Group cells by portal for an easier-to-scan matrix.
  const grouped = useMemo(() => {
    if (!data) return [];
    const ql = q.trim().toLowerCase();
    const byPortal = new Map();
    for (const c of data.cells) {
      if (ql && !(c.portal.includes(ql) || c.portal_name.toLowerCase().includes(ql))) continue;
      if (onlyEnabled === "enabled"  && !c.enabled) continue;
      if (onlyEnabled === "disabled" &&  c.enabled) continue;
      if (!byPortal.has(c.portal)) {
        byPortal.set(c.portal, { portal: c.portal, portal_name: c.portal_name, domain: c.domain, placements: [] });
      }
      byPortal.get(c.portal).placements.push(c);
    }
    return Array.from(byPortal.values()).sort((a, b) => a.portal_name.localeCompare(b.portal_name));
  }, [data, q, onlyEnabled]);

  if (!user) {
    return (
      <div className="min-h-screen flex items-center justify-center text-slate-600">
        <Link to="/login" className="underline">Sign in to continue</Link>
      </div>
    );
  }
  if (!isAdmin) {
    return (
      <div className="min-h-screen flex items-center justify-center text-slate-600">
        Admin access required.
      </div>
    );
  }

  const totals = data?.totals;

  return (
    <div className="min-h-screen bg-slate-50 flex flex-col" data-testid="admin-ads-page">
      <SocialPulseHeader />
      <main className="flex-1 max-w-6xl mx-auto px-4 py-8 w-full">
        {/* header */}
        <div className="flex items-center justify-between gap-4 mb-6 flex-wrap">
          <div>
            <Link to="/admin" className="inline-flex items-center gap-1 text-xs text-slate-500 hover:text-slate-800 mb-2">
              <ArrowLeft className="w-3 h-3" /> Back to admin
            </Link>
            <h1 className="font-serif text-3xl font-black text-slate-900 flex items-center gap-2">
              <Megaphone className="w-7 h-7 text-emerald-600" />
              Ad Network
            </h1>
            <p className="text-sm text-slate-500 mt-1">
              Social Pulse ad serving across {data?.cells?.length ? data.cells.length / 4 : "—"} sisters · cross-promo house ads only (v1)
            </p>
          </div>
          <div className="flex items-center gap-2">
            <Link
              to="/admin/ad-campaigns"
              data-testid="admin-ads-campaigns-link"
              className="inline-flex items-center gap-1.5 rounded-full ring-1 ring-slate-200 bg-white hover:bg-slate-50 text-slate-800 text-sm font-semibold px-4 py-2"
            >
              <Megaphone className="w-3.5 h-3.5 text-blue-600" /> Paid campaigns
            </Link>
            <button
              type="button"
              onClick={load}
              disabled={loading}
              data-testid="admin-ads-refresh"
              className="inline-flex items-center gap-1.5 rounded-full bg-slate-900 hover:bg-slate-800 disabled:opacity-50 text-white text-sm font-semibold px-4 py-2"
            >
              {loading ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <RefreshCw className="w-3.5 h-3.5" />}
              Refresh
            </button>
          </div>
        </div>

        {/* summary */}
        {totals && (
          <div className="grid grid-cols-2 sm:grid-cols-5 gap-3 mb-6" data-testid="admin-ads-summary">
            <SummaryPill label="Slots total"    value={totals.cells}       tone="slate"   />
            <SummaryPill label="Enabled"        value={totals.enabled}     tone="emerald" />
            <SummaryPill label="Disabled"       value={totals.disabled}    tone="rose"    />
            <SummaryPill label="24h impressions" value={totals.impressions.toLocaleString()} tone="violet" />
            <SummaryPill label="24h clicks"     value={totals.clicks.toLocaleString()}      tone="amber"   />
          </div>
        )}

        {/* filters */}
        <div className="flex gap-2 mb-4 flex-wrap">
          <div className="flex-1 min-w-[180px] relative">
            <Search className="w-4 h-4 text-slate-400 absolute left-3 top-1/2 -translate-y-1/2" />
            <input
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder="Search sister (name or slug)"
              data-testid="admin-ads-search"
              className="w-full rounded-full ring-1 ring-slate-200 bg-white pl-9 pr-4 py-2 text-sm outline-none"
            />
          </div>
          <select
            value={onlyEnabled}
            onChange={(e) => setOnlyEnabled(e.target.value)}
            data-testid="admin-ads-filter"
            className="rounded-full ring-1 ring-slate-200 bg-white px-4 py-2 text-sm outline-none"
          >
            <option value="all">All slots</option>
            <option value="enabled">Enabled only</option>
            <option value="disabled">Disabled only</option>
          </select>
        </div>

        {/* grid */}
        {loading ? (
          <div className="flex items-center justify-center py-12 text-slate-500 text-sm">
            <Loader2 className="w-4 h-4 animate-spin mr-2" /> Loading network grid…
          </div>
        ) : grouped.length === 0 ? (
          <div className="text-center py-12 text-slate-500 text-sm">No sisters match the current filter.</div>
        ) : (
          <div className="bg-white rounded-2xl ring-1 ring-slate-200 overflow-hidden" data-testid="admin-ads-grid">
            {grouped.map((p) => (
              <div key={p.portal} className="border-b border-slate-100 last:border-b-0" data-testid={`admin-ads-row-${p.portal}`}>
                <div className="flex items-center justify-between gap-3 px-4 py-3 bg-slate-50">
                  <div className="min-w-0">
                    <h3 className="font-serif font-bold text-slate-900 truncate">{p.portal_name}</h3>
                    {p.domain && (
                      <a href={p.domain} target="_blank" rel="noreferrer"
                         className="inline-flex items-center gap-1 text-xs text-slate-500 hover:text-slate-800">
                        <ExternalLink className="w-3 h-3" /> {p.domain.replace(/^https?:\/\//, "")}
                      </a>
                    )}
                  </div>
                  <code className="font-mono text-[10px] bg-slate-100 px-2 py-0.5 rounded">{p.portal}</code>
                </div>
                <div className="grid grid-cols-2 sm:grid-cols-4 gap-px bg-slate-100">
                  {p.placements.map((c) => {
                    const key = `${c.portal}|${c.placement}`;
                    const busy = togglingKey === key;
                    return (
                      <button
                        key={c.placement}
                        type="button"
                        disabled={busy}
                        onClick={() => toggleCell(c)}
                        data-testid={`admin-ads-cell-${c.portal}-${c.placement}`}
                        className={`text-left p-3 bg-white hover:bg-slate-50 disabled:opacity-60 transition-colors`}
                        title={`Click to ${c.enabled ? "disable" : "enable"} this slot`}
                      >
                        <div className="flex items-center justify-between gap-2">
                          <span className="font-mono text-[10px] font-bold text-slate-500">{c.placement}</span>
                          {busy ? (
                            <Loader2 className="w-3.5 h-3.5 text-slate-400 animate-spin" />
                          ) : c.enabled ? (
                            <CheckCircle2 className="w-3.5 h-3.5 text-emerald-600" />
                          ) : (
                            <XCircle className="w-3.5 h-3.5 text-rose-500" />
                          )}
                        </div>
                        <div className="text-xs font-semibold text-slate-800 mt-0.5">{c.placement_name}</div>
                        <Sparkline hourly={c.hourly || []} />
                        <div className="mt-1.5 flex items-center gap-2 text-[11px] text-slate-500">
                          <TrendingUp className="w-3 h-3" />
                          <span><strong className="text-slate-900">{c.impressions.toLocaleString()}</strong> imp</span>
                          <span>·</span>
                          <span><strong className="text-slate-900">{c.clicks.toLocaleString()}</strong> clk</span>
                          {c.impressions > 0 && (
                            <>
                              <span>·</span>
                              <span>{c.ctr}% CTR</span>
                            </>
                          )}
                        </div>
                      </button>
                    );
                  })}
                </div>
              </div>
            ))}
          </div>
        )}
      </main>
      <SocialPulseFooter />
    </div>
  );
}


function Sparkline({ hourly }) {
  // Tiny 24-bar impression sparkline. Hand-rolled SVG — no external
  // library — because the admin grid has up to 168 of these so even
  // a cheap Recharts mount adds up fast.
  const bins = Array.isArray(hourly) && hourly.length === 24 ? hourly : new Array(24).fill(0);
  const max = Math.max(1, ...bins);
  const width = 110;   // ~width of the slot cell at md
  const height = 18;
  const barW = width / 24;
  // The last 2 bars (now and -1h) get a hint of color to say "live".
  const total = bins.reduce((a, b) => a + b, 0);
  return (
    <div className="mt-2" data-testid="admin-ads-sparkline" title={`${total.toLocaleString()} impressions · trailing 24h`}>
      <svg width={width} height={height} viewBox={`0 0 ${width} ${height}`} preserveAspectRatio="none">
        {bins.map((v, i) => {
          const h = Math.max(1, Math.round((v / max) * (height - 2)));
          const x = i * barW + 0.5;
          const y = height - h;
          const live = i >= 22;    // last 2 hours
          return (
            <rect
              key={i}
              x={x} y={y}
              width={Math.max(1, barW - 1)} height={h}
              fill={v === 0 ? "#e2e8f0" : live ? "#2563eb" : "#94a3b8"}
              rx={0.5}
            />
          );
        })}
      </svg>
    </div>
  );
}


function SummaryPill({ label, value, tone }) {
  const tones = {
    slate:   { bg: "bg-slate-50",   txt: "text-slate-700",   ring: "ring-slate-200"   },
    emerald: { bg: "bg-emerald-50", txt: "text-emerald-700", ring: "ring-emerald-200" },
    rose:    { bg: "bg-rose-50",    txt: "text-rose-700",    ring: "ring-rose-200"    },
    violet:  { bg: "bg-violet-50",  txt: "text-violet-700",  ring: "ring-violet-200"  },
    amber:   { bg: "bg-amber-50",   txt: "text-amber-700",   ring: "ring-amber-200"   },
  };
  const t = tones[tone] || tones.slate;
  return (
    <div className={`rounded-xl ${t.bg} ring-1 ${t.ring} px-3 py-2`}>
      <div className={`text-[10px] font-bold uppercase tracking-wide ${t.txt}`}>{label}</div>
      <div className="text-xl font-serif font-black text-slate-900 mt-0.5">{value}</div>
    </div>
  );
}
